import InputForm from "../../../../components/input/input-form.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import ProjectUtils from "../../../../utils/project-utils.js";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import {getCities, getCountries, getNeighborhoods, getStreets} from "../../../../utils/location-utils.js";
import Attach from "../../../../components/attach/attach.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import React, {useEffect, useRef, useState} from "react";
import EncounterService from "../../../../services/web/encounter-service.js";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import optionsUtils from "../../../../utils/options-utils.js";
import {MaskDefault} from "../../../../utils/mask.js";
import {normalizeCoordinate} from "../../individual/form/resources/map-utils.js";
import useIndividualCatalogs from "../hooks/use-individual-catalogs.js";
import Button from "../../../../components/button/button.jsx";
import {FaPen, FaPlus, FaTrash} from "react-icons/fa";
import {AddressMapModal} from "../../individual/form/components/tabs/addresses.jsx";
import {Modal} from "../../../../components/modal/modal.jsx";
import {v4 as uuidv4} from "uuid";
import ConfirmationDeleteList from "../../../../components/utils/confirmation-delete-lists.jsx";
import InputFile from "../../../../components/input/input-file.jsx";
import {format} from "date-fns";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const getLegacyAttachId = (name = "") => {
    const parts = String(name).split(".");
    if (parts.length <= 1) return name;
    parts.pop();
    return parts.join(".");
};

const getExtensionFromName = (name = "") => {
    const parts = String(name).split(".");
    return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const normalizeCpf = (value = "") => String(value).replace(/\D/g, "");

const sexOptions = [
    {value: "MALE", label: "MASCULINO"},
    {value: "FEMALE", label: "FEMININO"},
];

const toOption = (value) => {
    if (!value) return null;
    return { value, label: value };
};

const normalizeSex = (value) => {
    if (!value) return null;

    if (value === "MASCULINO" || value === "MALE") return sexOptions[0];
    if (value === "FEMININO" || value === "FEMALE") return sexOptions[1];

    return null;
};

const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;

    let normalizedValue = value;
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        normalizedValue = `${value}T00:00:00`;
    } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        const [day, month, year] = value.split("/");
        normalizedValue = `${year}-${month}-${day}T00:00:00`;
    }

    const parsed = new Date(normalizedValue);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getCatalogOption = (options, value) => {
    if (value === null || value === undefined || value === "") return null;
    return options.find((option) => String(option.value) === String(value)) ?? {value, label: value};
};

const getPhoneFromReference = (data, fallbackPhone = "", fallbackProvider = null) => {
    const phones = Array.isArray(data?.phones) ? data.phones : [];
    const hasData = (item) => item?.data !== null && item?.data !== undefined && String(item.data).trim() !== "";
    const residential = phones.find((item) => item?.type === "RESIDENCIAL" && hasData(item));
    const firstValid = phones.find(hasData);
    const selectedPhone = residential ?? firstValid;

    if (selectedPhone) {
        return {
            phone: selectedPhone.data,
            phoneProvider: selectedPhone.phoneProvider ?? fallbackProvider,
        };
    }

    if (data?.phone !== null && data?.phone !== undefined && String(data.phone).trim() !== "") {
        return {
            phone: data.phone,
            phoneProvider: null,
        };
    }

    return {
        phone: fallbackPhone,
        phoneProvider: fallbackProvider,
    };
};

const mergeIndividualRegions = (currentItems, referenceItems) => {
    if (!Array.isArray(referenceItems) || referenceItems.length === 0) return currentItems;

    const merged = Array.isArray(currentItems) ? [...currentItems] : [];

    referenceItems.forEach((referenceItem) => {
        const hasItemId = referenceItem?.itemId !== null && referenceItem?.itemId !== undefined && referenceItem?.itemId !== "";

        if (hasItemId) {
            const existingIndex = merged.findIndex((item) =>
                item?.itemId !== null &&
                item?.itemId !== undefined &&
                String(item.itemId) === String(referenceItem.itemId)
            );

            if (existingIndex >= 0) {
                merged[existingIndex] = {
                    ...merged[existingIndex],
                    ...referenceItem,
                    itemId: merged[existingIndex].itemId,
                };
            } else {
                merged.push(referenceItem);
            }

            return;
        }

        const alreadyExists = merged.some((item) =>
            item?.local === referenceItem?.local &&
            item?.type === referenceItem?.type &&
            item?.description === referenceItem?.description
        );

        if (!alreadyExists) merged.push(referenceItem);
    });

    return merged;
};

const emptyIndividualRegion = () => ({
    itemId: null,
    local: "",
    type: "",
    description: "",
});

const emptyArticle = () => ({
    itemId: null,
    date: null,
    bo: "",
    article: "",
    attachId: null,
});

const sameAttachId = (first, second) =>
    first !== null && first !== undefined &&
    second !== null && second !== undefined &&
    String(first) === String(second);

const mergeArticlesFromReference = (currentArticles, referenceArticles, referenceAttaches) => {
    if (!Array.isArray(referenceArticles) || referenceArticles.length === 0) {
        return {
            articles: currentArticles,
            importedAttaches: [],
        };
    }

    const availableAttaches = Array.isArray(referenceAttaches) ? referenceAttaches : [];
    const importedAttaches = [];
    const importableArticles = referenceArticles.filter((article) => {
        if (!article?.attachId) return true;

        const attach = availableAttaches.find((item) => sameAttachId(item?.attachId, article.attachId));
        if (!attach) return false;

        importedAttaches.push(attach);
        return true;
    });
    const merged = Array.isArray(currentArticles) ? [...currentArticles] : [];

    importableArticles.forEach((referenceArticle) => {
        const hasItemId = referenceArticle?.itemId !== null && referenceArticle?.itemId !== undefined && referenceArticle?.itemId !== "";

        if (hasItemId) {
            const existingIndex = merged.findIndex((item) =>
                item?.itemId !== null &&
                item?.itemId !== undefined &&
                String(item.itemId) === String(referenceArticle.itemId)
            );

            if (existingIndex >= 0) {
                merged[existingIndex] = {
                    ...merged[existingIndex],
                    ...referenceArticle,
                    itemId: merged[existingIndex].itemId,
                };
            } else {
                merged.push(referenceArticle);
            }

            return;
        }

        const alreadyExists = merged.some((item) =>
            item?.date === referenceArticle?.date &&
            item?.bo === referenceArticle?.bo &&
            item?.article === referenceArticle?.article &&
            item?.attachId === referenceArticle?.attachId
        );

        if (!alreadyExists) merged.push(referenceArticle);
    });

    return {
        articles: merged,
        importedAttaches,
    };
};

const mergeImportedAttaches = (currentAttaches, importedAttaches) => {
    if (!importedAttaches.length) return currentAttaches;

    const merged = Array.isArray(currentAttaches) ? [...currentAttaches] : [];

    importedAttaches.forEach((attach) => {
        if (!merged.some((item) => sameAttachId(item?.attachId, attach?.attachId))) {
            merged.push(attach);
        }
    });

    return merged;
};

const parseIndividualCpfResponse = (responseData) => {
    if (!responseData) return {};

    const payload = responseData?.data ?? responseData;

    if (typeof payload === "string") {
        try {
            return JSON.parse(payload);
        } catch (error) {
            console.error("Erro ao converter data do indivíduo:", error);
            return {};
        }
    }

    return payload;
};

const buildIndividualDataFromCpfResponse = (responseData, currentData) => {
    const parsedData = parseIndividualCpfResponse(responseData);
    const importedPhone = getPhoneFromReference(
        parsedData,
        currentData?.phone ?? "",
        currentData?.phoneProvider ?? null
    );
    const firstAddress = parsedData?.addresses?.find((item) => item?.type !== "COMERCIAL") ?? parsedData?.address ?? {};
    const referenceAttaches = parsedData?.attachs ?? parsedData?.attaches;
    const importedArticleData = mergeArticlesFromReference(
        currentData?.articles,
        parsedData?.articles,
        referenceAttaches
    );

    return {
        ...(currentData ?? {}),
        cpf: parsedData?.cpf ?? currentData?.cpf ?? "",
        name: parsedData?.name ?? currentData?.name ?? "",
        socialName: parsedData?.socialName ?? currentData?.socialName ?? "",
        nickname: parsedData?.nickname ?? currentData?.nickname ?? "",
        mother: parsedData?.mother ?? currentData?.mother ?? "",
        father: parsedData?.father ?? currentData?.father ?? "",
        rg: parsedData?.rg ?? currentData?.rg ?? "",
        emitterRg: parsedData?.emitterRg ?? currentData?.emitterRg ?? "",
        criminalRg: parsedData?.criminalRg ?? currentData?.criminalRg ?? "",
        cnh: parsedData?.cnh ?? currentData?.cnh ?? "",
        dtBirth: parseDate(parsedData?.dtBirth) ?? currentData?.dtBirth ?? null,

        sex: parsedData?.sex ? normalizeSex(parsedData.sex) : currentData?.sex ?? null,

        cityOfBirth: toOption(parsedData?.cityOfBirth) ?? currentData?.cityOfBirth ?? null,
        nationality: toOption(parsedData?.nationality) ?? currentData?.nationality ?? null,
        phone: importedPhone.phone,
        phoneProvider: importedPhone.phoneProvider,
        maritalStatus: parsedData?.maritalStatus ?? currentData?.maritalStatus ?? null,
        mentalDisorder: parsedData?.mentalDisorder ?? currentData?.mentalDisorder ?? null,
        alcoholDrugDependency: parsedData?.alcoholDrugDependency ?? currentData?.alcoholDrugDependency ?? null,
        height: parsedData?.height ?? currentData?.height ?? "",
        color: parsedData?.color ?? currentData?.color ?? null,
        bodyShape: parsedData?.bodyShape ?? currentData?.bodyShape ?? null,
        eyes: parsedData?.eyes ?? currentData?.eyes ?? null,
        hairType: parsedData?.hairType ?? currentData?.hairType ?? null,
        hairColor: parsedData?.hairColor ?? currentData?.hairColor ?? null,
        hairSize: parsedData?.hairSize ?? currentData?.hairSize ?? null,
        roleCrime: parsedData?.roleCrime ?? currentData?.roleCrime ?? null,
        situation: parsedData?.situation ?? currentData?.situation ?? null,
        educationDegree: parsedData?.educationDegree ?? currentData?.educationDegree ?? null,
        occupation: parsedData?.occupation ?? currentData?.occupation ?? null,
        socialNetwork: parsedData?.socialNetwork ?? currentData?.socialNetwork ?? null,
        information: parsedData?.information ?? currentData?.information ?? null,
        individualRegionWrapper: mergeIndividualRegions(
            currentData?.individualRegionWrapper,
            parsedData?.individualRegionWrapper
        ),
        articles: importedArticleData.articles,

        homeless: parsedData?.homeless ?? currentData?.homeless ?? false,

        attaches: mergeImportedAttaches(currentData?.attaches, importedArticleData.importedAttaches),
        faceAttach: currentData?.faceAttach ?? null,
        documentAttach: currentData?.documentAttach ?? null,

        address: {
            ...(currentData?.address ?? {}),
            type: firstAddress?.type ?? currentData?.address?.type ?? null,
            date: parseDate(firstAddress?.date) ?? currentData?.address?.date ?? null,
            address: toOption(firstAddress?.address) ?? currentData?.address?.address ?? null,
            number: firstAddress?.number ?? currentData?.address?.number ?? "",
            neighborhood: toOption(firstAddress?.neighborhood) ?? currentData?.address?.neighborhood ?? null,
            city: toOption(firstAddress?.city) ?? currentData?.address?.city ?? null,
            complement: firstAddress?.complement ?? currentData?.address?.complement ?? "",
            zipCode: firstAddress?.zipCode ?? currentData?.address?.zipCode ?? "",
            lat: normalizeCoordinate(firstAddress?.lat) ?? currentData?.address?.lat ?? null,
            lng: normalizeCoordinate(firstAddress?.lng) ?? currentData?.address?.lng ?? null,
            notEmpty: parsedData?.homeless ?? currentData?.address?.notEmpty ?? false,
        },
    };
};

function EncounterIndividualsForm({ data, formKey, onChange }){
    const ignoreInitialAttachSyncRef = useRef(false);
    const [openMapModal, setOpenMapModal] = useState(false);
    const [mapPosition, setMapPosition] = useState(null);
    const [individualRegionModal, setIndividualRegionModal] = useState({
        open: false,
        index: null,
        originalItemId: null,
        data: emptyIndividualRegion(),
    });
    const [articleModal, setArticleModal] = useState({
        open: false,
        index: null,
        originalItemId: null,
        data: emptyArticle(),
        pendingAttach: null,
    });
    const {
        individualColors,
        bodyShapes,
        eyeColors,
        hairTypes,
        hairColors,
        hairSizes,
        situations,
        educationDegrees,
        individualRegions,
        individualRegionDetails,
        systemNatures,
        phoneProviders,
    } = useIndividualCatalogs();

    const latestDataRef = useRef(data);

    useEffect(() => {
        latestDataRef.current = data;
    }, [data]);

    const updateField = (field, value) => {
        onChange?.({
            [field]: value,
        });
    };

    const updateAddressField = (field, value) => {
        onChange?.({
            address: {
                [field]: value,
            },
        });
    };

    const openMap = () => {
        setMapPosition(null);
        setOpenMapModal(true);
    };

    const confirmMapLocation = (position) => {
        onChange?.({
            address: {
                lat: normalizeCoordinate(position?.lat),
                lng: normalizeCoordinate(position?.lng),
            },
        });
    };

    const closeIndividualRegionModal = () => setIndividualRegionModal({
        open: false,
        index: null,
        originalItemId: null,
        data: emptyIndividualRegion(),
    });

    const openNewIndividualRegion = () => setIndividualRegionModal({
        open: true,
        index: null,
        originalItemId: null,
        data: emptyIndividualRegion(),
    });

    const openEditIndividualRegion = (region, index) => setIndividualRegionModal({
        open: true,
        index,
        originalItemId: region?.itemId ?? null,
        data: {
            ...region,
            itemId: region?.itemId ?? null,
            local: region?.local ?? "",
            type: region?.type ?? "",
            description: region?.description ?? "",
        },
    });

    const saveIndividualRegion = () => {
        const {data: region, index, originalItemId} = individualRegionModal;
        const description = String(region?.description ?? "").trim();

        if (!region?.local) {
            ProjectUtils.swalErrorMessage("", 'O campo "Local" é obrigatório.', "Ok");
            return;
        }

        if (!region?.type) {
            ProjectUtils.swalErrorMessage("", 'O campo "Tipo" é obrigatório.', "Ok");
            return;
        }

        if (!description) {
            ProjectUtils.swalErrorMessage("", 'O campo "Descrição" é obrigatório.', "Ok");
            return;
        }

        const currentItems = Array.isArray(data?.individualRegionWrapper)
            ? data.individualRegionWrapper
            : [];
        const itemId = region?.itemId === null || region?.itemId === undefined || region?.itemId === ""
            ? uuidv4()
            : region.itemId;
        const savedRegion = {
            itemId,
            local: region.local,
            type: region.type,
            description,
        };

        if (index === null) {
            updateField("individualRegionWrapper", [...currentItems, savedRegion]);
        } else {
            const targetIndex = originalItemId !== null && originalItemId !== undefined
                ? currentItems.findIndex((item) => String(item?.itemId) === String(originalItemId))
                : index;

            updateField(
                "individualRegionWrapper",
                currentItems.map((item, itemIndex) => itemIndex === targetIndex ? savedRegion : item)
            );
        }

        closeIndividualRegionModal();
    };

    const removeIndividualRegion = (index) => {
        ConfirmationDeleteList(() => {
            const currentItems = Array.isArray(data?.individualRegionWrapper)
                ? data.individualRegionWrapper
                : [];

            updateField(
                "individualRegionWrapper",
                currentItems.filter((_, itemIndex) => itemIndex !== index)
            );
        });
    };

    const closeArticleModal = () => setArticleModal({
        open: false,
        index: null,
        originalItemId: null,
        data: emptyArticle(),
        pendingAttach: null,
    });

    const openNewArticle = () => setArticleModal({
        open: true,
        index: null,
        originalItemId: null,
        data: emptyArticle(),
        pendingAttach: null,
    });

    const openEditArticle = (article, index) => setArticleModal({
        open: true,
        index,
        originalItemId: article?.itemId ?? null,
        data: {
            ...article,
            itemId: article?.itemId ?? null,
            date: article?.date ?? null,
            bo: article?.bo ?? "",
            article: article?.article ?? "",
            attachId: article?.attachId ?? null,
        },
        pendingAttach: null,
    });

    const isAttachReferencedElsewhere = (attachId, excludedArticleIndex) => {
        if (!attachId) return false;
        if (sameAttachId(data?.faceAttach, attachId) || sameAttachId(data?.documentAttach, attachId)) return true;

        return (data?.articles ?? []).some((article, index) =>
            index !== excludedArticleIndex && sameAttachId(article?.attachId, attachId)
        );
    };

    const removeExclusiveArticleAttach = (attaches, attachId, excludedArticleIndex) => {
        if (!attachId || isAttachReferencedElsewhere(attachId, excludedArticleIndex)) return attaches;
        return attaches.filter((attach) => !sameAttachId(getAttachId(attach), attachId));
    };

    const setArticleFile = (file) => {
        const rawFile = file?.file;
        if (!(rawFile instanceof File)) return;

        if (rawFile.size > 20 * 1024 * 1024) {
            ProjectUtils.swalErrorMessage("", "Tamanho máximo de arquivo excedido. Limite: 20MB", "Ok");
            return;
        }

        const extension = getExtensionFromName(rawFile.name) || rawFile.type?.split("/")?.[1];
        const temporaryAttachId = `attach-article-${uuidv4()}`;
        const renamedFile = new File(
            [rawFile],
            `${temporaryAttachId}.${extension}`,
            {type: rawFile.type}
        );

        renamedFile.attachId = temporaryAttachId;
        renamedFile.extension = extension;
        renamedFile.url = file.url ?? URL.createObjectURL(rawFile);
        renamedFile.new = true;

        setArticleModal((current) => ({
            ...current,
            data: {...current.data, attachId: temporaryAttachId},
            pendingAttach: renamedFile,
        }));
    };

    const removeArticleFile = () => setArticleModal((current) => ({
        ...current,
        data: {...current.data, attachId: null},
        pendingAttach: null,
    }));

    const saveArticle = () => {
        const {data: article, index, originalItemId, pendingAttach} = articleModal;

        if (!article?.article) {
            ProjectUtils.swalErrorMessage("", "O campo \"Natureza\" é obrigatório.", "Ok");
            return;
        }

        if (article.date && article.date > format(new Date(), "yyyy-MM-dd")) {
            ProjectUtils.swalErrorMessage("", "A data não pode ser maior que a data de hoje.", "Ok");
            return;
        }

        const currentArticles = Array.isArray(data?.articles) ? data.articles : [];
        let currentAttaches = Array.isArray(data?.attaches) ? data.attaches : [];
        const previousAttachId = index === null ? null : currentArticles[index]?.attachId;
        const itemId = article?.itemId === null || article?.itemId === undefined || article?.itemId === ""
            ? uuidv4()
            : article.itemId;

        if (pendingAttach) {
            currentAttaches = removeExclusiveArticleAttach(currentAttaches, previousAttachId, index);
            currentAttaches = [
                ...currentAttaches.filter((attach) => !sameAttachId(getAttachId(attach), pendingAttach.attachId)),
                pendingAttach,
            ];
        } else if (!article.attachId && previousAttachId) {
            currentAttaches = removeExclusiveArticleAttach(currentAttaches, previousAttachId, index);
        }

        const savedArticle = {
            itemId,
            date: article.date ?? null,
            bo: article.bo ?? null,
            article: article.article,
            attachId: article.attachId ?? null,
        };

        let nextArticles;
        if (index === null) {
            nextArticles = [...currentArticles, savedArticle];
        } else {
            const targetIndex = originalItemId !== null && originalItemId !== undefined
                ? currentArticles.findIndex((item) => String(item?.itemId) === String(originalItemId))
                : index;
            nextArticles = currentArticles.map((item, itemIndex) => itemIndex === targetIndex ? savedArticle : item);
        }

        onChange?.({
            articles: nextArticles,
            attaches: currentAttaches,
        });
        closeArticleModal();
    };

    const removeArticle = (index) => {
        ConfirmationDeleteList(() => {
            const currentArticles = Array.isArray(data?.articles) ? data.articles : [];
            const currentAttaches = Array.isArray(data?.attaches) ? data.attaches : [];
            const article = currentArticles[index];

            onChange?.({
                articles: currentArticles.filter((_, articleIndex) => articleIndex !== index),
                attaches: removeExclusiveArticleAttach(currentAttaches, article?.attachId, index),
            });
        });
    };

    const handleSetAttaches = (next) => {
        const currentAttaches = Array.isArray(data?.attaches) ? data.attaches : [];
        const resolvedAttaches =
            typeof next === "function" ? next(currentAttaches) : next;

        const normalizedAttaches = (Array.isArray(resolvedAttaches) ? resolvedAttaches : []).map((attach) => {
            if (attach instanceof File && attach?.new) {
                attach.attachId = getLegacyAttachId(attach.name);
            }

            return attach;
        });

        updateField("attaches", normalizedAttaches);
    };

    const handleFaceAttachChange = (file) => {
        const currentFaceAttach = latestDataRef.current?.faceAttach ?? null;

        if (file == null) {
            return;
        }

        if (currentFaceAttach === file) {
            return;
        }

        onChange?.({
            ...(latestDataRef.current ?? {}),
            faceAttach: file,
        });
    };

    const getAttachId = (attach) => {
        if (!attach) return null;

        if (attach instanceof File) {
            return attach.attachId ?? getLegacyAttachId(attach.name);
        }

        if (typeof attach === "object") {
            return attach.attachId ?? attach.attach_id ?? attach.id ?? null;
        }

        return attach;
    };

    const handleRemoveFaceAttach = () => {
        if (!data?.faceAttach) return;

        const faceAttachId = getAttachId(data.faceAttach);

        const nextAttaches = (data?.attaches ?? []).filter((attach) => {
            const attachId = getAttachId(attach);

            return attachId !== faceAttachId;
        });

        onChange?.({
            ...(data ?? {}),
            faceAttach: null,
            attaches: nextAttaches,
        });
    };

    const handleCpfChange = async (value) => {
        updateField("cpf", value);

        const normalizedCpf = normalizeCpf(value);
        if (normalizedCpf.length !== 11) return;

        try {
            const response = await EncounterService.getIndividualCpf(normalizedCpf);
            if (!response?.data) return;

            onChange?.(buildIndividualDataFromCpfResponse(response.data, latestDataRef.current));
        } catch (error) {
            console.error("Erro ao buscar CPF:", error);
        }
    };

    const faceAttachId = getAttachId(data?.faceAttach);
    const articleAttach = (data?.attaches ?? []).find((attach) =>
        sameAttachId(getAttachId(attach), articleModal.data.attachId)
    );
    const articleModalFile = articleModal.pendingAttach ?? (articleAttach
        ? articleAttach instanceof File
            ? articleAttach
            : {
                ...articleAttach,
                url: `${API_KEEPER}/ged/attach/${getAttachId(articleAttach)}`,
            }
        : null);

    const visibleAttaches = (data?.attaches ?? []).filter((attach) => {
        const attachId = getAttachId(attach);

        return attachId !== faceAttachId;
    });

    return (
        <div className="border border-[#e5e7eb] rounded-md p-4 space-y-3">
            <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                <div className="col-span-2">
                    <Attach
                        onChangeFile={handleFaceAttachChange}
                        removeFile={handleRemoveFaceAttach}
                        itemId={`face-${formKey}`}
                        attachId={data?.faceAttach ?? null}
                        attachedButtonDisabled={!!data?.id && data?.faceAttach == null}
                        attachedButtonName="Foto da face"
                        modalTitle="Foto da face"
                    />
                </div>
                <InputForm
                    id={`cpf-${formKey}`}
                    label="CPF *"
                    classNameWrapper="col-span-2"
                    mask={"000.000.000-00"}
                    value={ProjectUtils.formatCpfToView(data?.cpf)}
                    onChange={(e) => handleCpfChange(e.target.value)}
                />
                <InputForm
                    id={`name-${formKey}`}
                    label="Nome *"
                    classNameWrapper="col-span-4"
                    value={data?.name ?? ""}
                    onChange={(e) => updateField("name", e.target.value)}
                />
                <InputForm
                    id={`socialName-${formKey}`}
                    label="Nome social"
                    classNameWrapper="col-span-4"
                    value={data?.socialName ?? ""}
                    onChange={(e) => updateField("socialName", e.target.value)}
                />
                <InputForm
                    id={`nickname-${formKey}`}
                    label="Apelido"
                    classNameWrapper="col-span-4"
                    value={data?.nickname ?? ""}
                    onChange={(e) => updateField("nickname", e.target.value)}
                />
                <SelectComponent
                    id={`sex-${formKey}`}
                    label="Sexo"
                    classNameWrapper="col-span-4"
                    options={sexOptions}
                    value={data?.sex}
                    onChange={(val) => updateField("sex", val ?? null)}
                />
                <InputDateTime
                    id={`dtBirth-${formKey}`}
                    label="Data de nascimento"
                    classNameWrapper="col-span-4"
                    dateFormat="L"
                    selected={data?.dtBirth}
                    onChange={(value) => updateField("dtBirth", value ?? null)}
                />

                <div className="col-span-full border-t border-[#e5e7eb] pt-2">
                    <h4 className="text-sm font-semibold text-gray-500">Documentos</h4>
                </div>

                <InputForm
                    id={`rg-${formKey}`}
                    label="RG"
                    classNameWrapper="col-span-3"
                    value={data?.rg ?? ""}
                    onChange={(e) => updateField("rg", e.target.value)}
                />
                <InputForm
                    id={`emitterRg-${formKey}`}
                    label="Emissor"
                    classNameWrapper="col-span-2"
                    value={data?.emitterRg ?? ""}
                    onChange={(e) => updateField("emitterRg", e.target.value)}
                />
                <InputForm
                    id={`criminalRg-${formKey}`}
                    label="RG criminal"
                    classNameWrapper="col-span-3"
                    value={data?.criminalRg ?? ""}
                    onChange={(e) => updateField("criminalRg", e.target.value)}
                />
                <InputForm
                    id={`cnh-${formKey}`}
                    label="CNH"
                    classNameWrapper="col-span-4"
                    value={data?.cnh ?? ""}
                    onChange={(e) => updateField("cnh", e.target.value)}
                />

                <div className="col-span-full border-t border-[#e5e7eb] pt-2">
                    <h4 className="text-sm font-semibold text-gray-500">Informações adicionais</h4>
                </div>

                <SelectComponent
                    id={`maritalStatus-${formKey}`}
                    label="Estado civil"
                    classNameWrapper="col-span-3"
                    options={optionsUtils.maritalStatus}
                    value={getCatalogOption(optionsUtils.maritalStatus, data?.maritalStatus)}
                    onChange={(value) => updateField("maritalStatus", value?.value ?? null)}
                />
                <SelectComponent
                    id={`nationality-${formKey}`}
                    label="Nacionalidade"
                    classNameWrapper="col-span-4"
                    isAsync={true}
                    loadOptions={getCountries}
                    defaultOptions={[]}
                    value={data?.nationality}
                    onChange={(val) => updateField("nationality", val ?? null)}
                />
                <SelectComponent
                    id={`cityOfBirth-${formKey}`}
                    label="Naturalidade"
                    classNameWrapper="col-span-5"
                    isAsync={true}
                    loadOptions={getCities}
                    defaultOptions={[]}
                    value={data?.cityOfBirth}
                    onChange={(val) => updateField("cityOfBirth", val ?? null)}
                />
                <InputForm
                    id={`phone-${formKey}`}
                    label="Telefone"
                    classNameWrapper="col-span-3"
                    mask={MaskDefault.phone}
                    value={data?.phone ?? ""}
                    onChange={(e) => updateField("phone", e.target.value)}
                />
                <SelectComponent
                    id={`phoneProvider-${formKey}`}
                    label="Operadora (Telefone)"
                    classNameWrapper="col-span-3"
                    options={phoneProviders}
                    value={getCatalogOption(phoneProviders, data?.phoneProvider)}
                    onChange={(value) => updateField("phoneProvider", value?.value ?? null)}
                />
                <InputForm
                    id={`mother-${formKey}`}
                    label="Nome da mãe"
                    classNameWrapper="col-span-3"
                    value={data?.mother ?? ""}
                    onChange={(e) => updateField("mother", e.target.value)}
                />
                <InputForm
                    id={`father-${formKey}`}
                    label="Nome do pai"
                    classNameWrapper="col-span-3"
                    value={data?.father ?? ""}
                    onChange={(e) => updateField("father", e.target.value)}
                />
                <div className="col-span-full flex items-end gap-4 pb-2">
                    <InputSwitch
                        id={`mentalDisorder-${formKey}`}
                        label="Possui transtorno mental"
                        checked={data?.mentalDisorder === true}
                        onChange={(value) => updateField("mentalDisorder", value)}
                    />
                    <InputSwitch
                        id={`alcoholDrugDependency-${formKey}`}
                        label="Dependência álcool/drogas"
                        checked={data?.alcoholDrugDependency === true}
                        onChange={(value) => updateField("alcoholDrugDependency", value)}
                    />
                </div>
            </div>

            <div className="flex flex-col space-y-3">
                <div className="border-t border-[#e5e7eb] pt-2">
                    <h4 className="text-sm font-semibold text-gray-500">Características</h4>
                </div>
                <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                    <InputForm
                        id={`height-${formKey}`}
                        label="Altura (cm)"
                        type="number"
                        integerOnly
                        classNameWrapper="col-span-2"
                        value={data?.height ?? ""}
                        onChange={(e) => updateField("height", e.target.value)}
                    />
                    <SelectComponent
                        id={`color-${formKey}`}
                        label="Cútis"
                        classNameWrapper="col-span-2"
                        options={individualColors}
                        value={getCatalogOption(individualColors, data?.color)}
                        onChange={(value) => updateField("color", value?.value ?? null)}
                    />
                    <SelectComponent
                        id={`bodyShape-${formKey}`}
                        label="Compleição"
                        classNameWrapper="col-span-2"
                        options={bodyShapes}
                        value={getCatalogOption(bodyShapes, data?.bodyShape)}
                        onChange={(value) => updateField("bodyShape", value?.value ?? null)}
                    />
                    <SelectComponent
                        id={`eyes-${formKey}`}
                        label="Cor dos olhos"
                        classNameWrapper="col-span-2"
                        options={eyeColors}
                        value={getCatalogOption(eyeColors, data?.eyes)}
                        onChange={(value) => updateField("eyes", value?.value ?? null)}
                    />
                    <SelectComponent
                        id={`hairType-${formKey}`}
                        label="Tipo dos cabelos"
                        classNameWrapper="col-span-4"
                        options={hairTypes}
                        value={getCatalogOption(hairTypes, data?.hairType)}
                        onChange={(value) => updateField("hairType", value?.value ?? null)}
                    />
                    <SelectComponent
                        id={`hairColor-${formKey}`}
                        label="Cor dos cabelos"
                        classNameWrapper="col-span-6"
                        options={hairColors}
                        value={getCatalogOption(hairColors, data?.hairColor)}
                        onChange={(value) => updateField("hairColor", value?.value ?? null)}
                    />
                    <SelectComponent
                        id={`hairSize-${formKey}`}
                        label="Comprimento dos cabelos"
                        classNameWrapper="col-span-6"
                        options={hairSizes}
                        value={getCatalogOption(hairSizes, data?.hairSize)}
                        onChange={(value) => updateField("hairSize", value?.value ?? null)}
                    />
                </div>
            </div>

            <div className="flex flex-col space-y-3">
                <div className="flex items-center gap-2 border-t border-[#e5e7eb] pt-1">
                    <h4 className="text-sm font-semibold text-gray-500 translate-y-1">Outras características</h4>
                    <Button
                        type="button"
                        className="h-7.5! w-7.5 p-0! translate-y-1"
                        title="Adicionar característica"
                        onClick={openNewIndividualRegion}
                    >
                        <FaPlus/>
                    </Button>
                </div>

                {(data?.individualRegionWrapper ?? []).map((region, index) => (
                    <div
                        key={`individual-region-${region?.itemId ?? "legacy"}-${index}`}
                        className="grid grid-cols-12 gap-2 items-center border border-[#e5e7eb] rounded-md p-2 text-sm"
                    >
                        <span className="col-span-3"><strong>Local:</strong> {getCatalogOption(individualRegions, region?.local)?.label ?? "-"}</span>
                        <span className="col-span-3"><strong>Tipo:</strong> {getCatalogOption(individualRegionDetails, region?.type)?.label ?? "-"}</span>
                        <span className="col-span-4"><strong>Descrição:</strong> {region?.description ?? "-"}</span>
                        <div className="col-span-2 flex justify-end gap-1">
                            <Button
                                type="button"
                                className="h-7.5! w-7.5 p-0!"
                                title="Editar característica"
                                onClick={() => openEditIndividualRegion(region, index)}
                            >
                                <FaPen/>
                            </Button>
                            <Button
                                type="button"
                                target="danger"
                                className="h-7.5! w-7.5 p-0!"
                                title="Excluir característica"
                                onClick={() => removeIndividualRegion(index)}
                            >
                                <FaTrash/>
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            <Modal
                overflow={true}
                isCloseClick={false}
                open={individualRegionModal.open}
                setOpen={closeIndividualRegionModal}
                title="Característica"
                className="w-4/12"
            >
                <Modal.Body>
                    <div className="space-y-3">
                        <SelectComponent
                            value={getCatalogOption(individualRegions, individualRegionModal.data.local)}
                            options={individualRegions}
                            label="Local *"
                            onChange={(selected) => setIndividualRegionModal((current) => ({
                                ...current,
                                data: {...current.data, local: selected?.value ?? ""},
                            }))}
                        />
                        <SelectComponent
                            value={getCatalogOption(individualRegionDetails, individualRegionModal.data.type)}
                            options={individualRegionDetails}
                            label="Tipo *"
                            onChange={(selected) => setIndividualRegionModal((current) => ({
                                ...current,
                                data: {...current.data, type: selected?.value ?? ""},
                            }))}
                        />
                        <InputTextarea
                            textAreaClass="h-24"
                            label="Descrição *"
                            value={individualRegionModal.data.description}
                            onChange={({target: {value}}) => setIndividualRegionModal((current) => ({
                                ...current,
                                data: {...current.data, description: value},
                            }))}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" onClick={saveIndividualRegion}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                overflow={true}
                isCloseClick={false}
                open={articleModal.open}
                setOpen={closeArticleModal}
                title="Artigo"
                className="w-3/12"
            >
                <Modal.Body>
                    <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                        <InputDateTime
                            label="Data"
                            classNameWrapper="col-span-12"
                            selected={articleModal.data.date}
                            onChange={(date) => setArticleModal((current) => ({
                                ...current,
                                data: {...current.data, date: date ? format(date, "yyyy-MM-dd") : null},
                            }))}
                            dateFormat="L"
                        />
                        <InputForm
                            label="Boletim"
                            classNameWrapper="col-span-12"
                            value={articleModal.data.bo ?? ""}
                            onChange={({target: {value}}) => setArticleModal((current) => ({
                                ...current,
                                data: {...current.data, bo: value},
                            }))}
                        />
                        <SelectComponent
                            value={getCatalogOption(systemNatures, articleModal.data.article)}
                            options={systemNatures}
                            label="Natureza *"
                            classNameWrapper="col-span-12"
                            onChange={(selected) => setArticleModal((current) => ({
                                ...current,
                                data: {...current.data, article: selected?.value ?? ""},
                            }))}
                        />
                        <InputFile
                            file={articleModalFile}
                            setFile={setArticleFile}
                            removeFile={removeArticleFile}
                            title="Anexar arquivo"
                            titleWithFile="Visualizar anexo"
                            accept="image/*,application/pdf"
                            className="col-span-6"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="button" onClick={saveArticle}>Confirmar</Button>
                </Modal.Footer>
            </Modal>

            <div className="flex flex-col space-y-3">
                <div className="flex border-t border-[#e5e7eb] pt-2">
                    <h4 className="text-sm font-semibold text-gray-500">Endereço</h4>
                </div>
                <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-12 flex p-1 translate-y-1">
                        <InputCheckbox
                            label="Situação de rua"
                            checked={data?.address?.notEmpty ?? false}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                onChange?.({
                                    ...data,
                                    homeless: checked,
                                    address: {
                                        ...data.address,
                                        notEmpty: checked,
                                    },
                                });
                            }}
                        />
                    </div>
                    <SelectComponent
                        id={`address-${formKey}`}
                        label="Logradouro"
                        classNameWrapper="col-span-6"
                        isAsync={true}
                        loadOptions={getStreets}
                        defaultOptions={[]}
                        value={data?.address?.address}
                        onChange={(val) => updateAddressField("address", val ?? null)}
                    />
                    <InputForm
                        id={`number-${formKey}`}
                        label="Número"
                        classNameWrapper="col-span-1"
                        value={data?.address?.number ?? ""}
                        onChange={(e) => updateAddressField("number", e.target.value)}
                    />
                    <SelectComponent
                        id={`neighborhood-${formKey}`}
                        label="Bairro"
                        classNameWrapper="col-span-5"
                        isAsync={true}
                        loadOptions={getNeighborhoods}
                        defaultOptions={[]}
                        value={data?.address?.neighborhood}
                        onChange={(val) => updateAddressField("neighborhood", val ?? null)}
                    />
                    <SelectComponent
                        id={`city-${formKey}`}
                        label="Município"
                        classNameWrapper="col-span-4"
                        isAsync={true}
                        loadOptions={getCities}
                        defaultOptions={[]}
                        value={data?.address?.city}
                        onChange={(val) => updateAddressField("city", val ?? null)}
                    />
                    <InputForm
                        id={`complement-${formKey}`}
                        label="Complemento"
                        classNameWrapper="col-span-6"
                        value={data?.address?.complement ?? ""}
                        onChange={(e) => updateAddressField("complement", e.target.value)}
                    />
                    <InputForm
                        id={`zipCode-${formKey}`}
                        label="CEP"
                        classNameWrapper="col-span-2"
                        value={data?.address?.zipCode ?? ""}
                        onChange={(e) => updateAddressField("zipCode", e.target.value)}
                        mask={"00000-000"}
                    />
                    <SelectComponent
                        id={`addressType-${formKey}`}
                        label="Tipo"
                        classNameWrapper="col-span-2"
                        options={optionsUtils.addressType}
                        value={getCatalogOption(optionsUtils.addressType, data?.address?.type)}
                        onChange={(value) => updateAddressField("type", value?.value ?? null)}
                    />
                    <InputDateTime
                        id={`addressDate-${formKey}`}
                        label="Data"
                        classNameWrapper="col-span-2"
                        dateFormat="L"
                        selected={data?.address?.date ?? null}
                        onChange={(value) => updateAddressField("date", value ?? null)}
                    />
                    <InputForm
                        id={`latitude-${formKey}`}
                        label="Latitude"
                        type="number"
                        classNameWrapper="col-span-3"
                        value={data?.address?.lat ?? ""}
                        onChange={(e) => updateAddressField("lat", e.target.value === "" ? null : e.target.value)}
                        onBlur={(e) => updateAddressField("lat", normalizeCoordinate(e.target.value))}
                    />
                    <InputForm
                        id={`longitude-${formKey}`}
                        label="Longitude"
                        type="number"
                        classNameWrapper="col-span-3"
                        value={data?.address?.lng ?? ""}
                        onChange={(e) => updateAddressField("lng", e.target.value === "" ? null : e.target.value)}
                        onBlur={(e) => updateAddressField("lng", normalizeCoordinate(e.target.value))}
                    />
                    <div className="col-span-2 flex items-end">
                        <Button
                            className="w-full px-4!"
                            onClick={openMap}
                            title="Selecionar no mapa"
                        >
                            Mapa
                        </Button>
                    </div>
                </div>
            </div>

            <AddressMapModal
                open={openMapModal}
                setOpen={setOpenMapModal}
                address={{
                    ...data?.address,
                    address: data?.address?.address?.label ?? data?.address?.address?.value ?? data?.address?.address,
                    neighborhood: data?.address?.neighborhood?.label ?? data?.address?.neighborhood?.value ?? data?.address?.neighborhood,
                    city: data?.address?.city?.label ?? data?.address?.city?.value ?? data?.address?.city,
                }}
                mapPosition={mapPosition}
                setMapPosition={setMapPosition}
                onConfirm={confirmMapLocation}
            />

            <div className="flex flex-col space-y-3">
                <div className="border-t border-[#e5e7eb] pt-2">
                    <h4 className="text-sm font-semibold text-gray-500">Outras informações</h4>
                </div>
                <div className="grid grid-cols-12 gap-x-4 gap-y-3">
                    <SelectComponent
                        id={`educationDegree-${formKey}`}
                        label="Grau de instrução"
                        classNameWrapper="col-span-3"
                        options={educationDegrees}
                        value={getCatalogOption(educationDegrees, data?.educationDegree)}
                        onChange={(value) => updateField("educationDegree", value?.value ?? null)}
                    />
                    <InputForm
                        id={`occupation-${formKey}`}
                        label="Profissão"
                        classNameWrapper="col-span-2"
                        value={data?.occupation ?? ""}
                        onChange={(e) => updateField("occupation", e.target.value)}
                    />
                    <InputForm
                        id={`roleCrime-${formKey}`}
                        label="Função no crime"
                        classNameWrapper="col-span-3"
                        value={data?.roleCrime ?? ""}
                        onChange={(e) => updateField("roleCrime", e.target.value)}
                    />
                    <SelectComponent
                        id={`situation-${formKey}`}
                        label="Situação"
                        classNameWrapper="col-span-2"
                        options={situations}
                        value={getCatalogOption(situations, data?.situation)}
                        onChange={(value) => updateField("situation", value?.value ?? null)}
                    />
                    <InputForm
                        id={`socialNetwork-${formKey}`}
                        label="Rede social"
                        classNameWrapper="col-span-2"
                        value={data?.socialNetwork ?? ""}
                        onChange={(e) => updateField("socialNetwork", e.target.value)}
                    />
                </div>
                <InputTextarea
                    id={`information-${formKey}`}
                    label="Informações do indivíduo"
                    className="w-full"
                    value={data?.information ?? ""}
                    onChange={(e) => updateField("information", e.target.value)}
                    textAreaClass="h-[80px]"
                />
            </div>

            <div className="flex flex-col space-y-3">
                <div className="flex items-center gap-2 border-t border-[#e5e7eb] pt-1">
                    <h4 className="text-sm font-semibold text-gray-500 translate-y-1">Artigos indiciados</h4>
                    <Button
                        type="button"
                        className="h-7.5! w-7.5 p-0! translate-y-1"
                        title="Adicionar artigo"
                        onClick={openNewArticle}
                    >
                        <FaPlus/>
                    </Button>
                </div>

                {(data?.articles ?? []).map((article, index) => (
                    <div
                        key={`article-${article?.itemId ?? "legacy"}-${index}`}
                        className="grid grid-cols-12 gap-2 items-center border border-[#e5e7eb] rounded-md p-2 text-sm"
                    >
                        <span className="col-span-2"><strong>Data:</strong> {article?.date ? ProjectUtils.formatDate(article.date, "DD/MM/YYYY") : "-"}</span>
                        <span className="col-span-2"><strong>Boletim:</strong> {article?.bo || "-"}</span>
                        <span className="col-span-4"><strong>Natureza:</strong> {getCatalogOption(systemNatures, article?.article)?.label ?? "-"}</span>
                        <span className="col-span-2"><strong>Anexo:</strong> {article?.attachId ? "Sim" : "Não"}</span>
                        <div className="col-span-2 flex justify-end gap-1">
                            <Button
                                type="button"
                                className="h-7.5! w-7.5 p-0!"
                                title="Editar artigo"
                                onClick={() => openEditArticle(article, index)}
                            >
                                <FaPen/>
                            </Button>
                            <Button
                                type="button"
                                target="danger"
                                className="h-7.5! w-7.5 p-0!"
                                title="Excluir artigo"
                                onClick={() => removeArticle(index)}
                            >
                                <FaTrash/>
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-col">
                <div className="flex border-t border-[#e5e7eb] pt-2">
                    <h4 className="text-sm font-semibold text-gray-500">Anexos</h4>
                </div>
                <div className="flex w-full">
                    <div className="w-full">
                    <Dropzone
                        title="Anexos"
                        attaches={visibleAttaches}
                        setAttaches={handleSetAttaches}
                        withoutCard={true}
                        canRemove={(attach) => !(data?.articles ?? []).some((article) =>
                            sameAttachId(article?.attachId, getAttachId(attach))
                        )}
                    />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default React.memo(EncounterIndividualsForm)
